import { useCallback, useEffect, useRef, useState } from 'react';
import type { PushSubscriptionInput } from '@nargus/shared';
import { SW_URL, registerServiceWorker } from '@/lib/service-worker';
import {
  usePushKey,
  usePushStatus,
  useSubscribePush,
  useTestPush,
  useUnsubscribePush,
} from '@/resources/push/push.service';

export type PushState =
  | 'unsupported'
  | 'insecure'
  | 'unavailable'
  | 'denied'
  | 'off'
  | 'on';

function browserSupportsPush(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function toApplicationServerKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);

  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

function toInput(subscription: PushSubscription): PushSubscriptionInput {
  const keys = subscription.toJSON().keys;
  if (!keys?.p256dh || !keys.auth) {
    throw new Error('Push subscription is missing its encryption keys');
  }
  return { endpoint: subscription.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration(SW_URL);
  return (await registration?.pushManager.getSubscription()) ?? null;
}

export interface Push {
  state: PushState;
  permission: NotificationPermission;
  devices: number;
  ready: boolean;
  busy: boolean;
  error: unknown;
  testSent: boolean;
  enable: () => void;
  disable: () => void;
  sendTest: () => void;
}

export function usePush(): Push {
  const supported = browserSupportsPush();
  const secure = typeof window !== 'undefined' && window.isSecureContext;

  const key = usePushKey();
  const status = usePushStatus();
  const subscribe = useSubscribePush();
  const unsubscribe = useUnsubscribePush();
  const test = useTestPush();

  const [permission, setPermission] = useState<NotificationPermission>(() =>
    supported ? Notification.permission : 'default',
  );
  const [localError, setLocalError] = useState<unknown>(null);
  const [working, setWorking] = useState(false);
  const resynced = useRef(false);

  useEffect(() => {
    if (!supported || !secure || resynced.current) return;
    if (permission !== 'granted' || !status.data || status.data.devices > 0) return;

    resynced.current = true;
    void (async () => {
      const subscription = await currentSubscription();
      if (subscription) subscribe.mutate(toInput(subscription));
    })();
  }, [supported, secure, permission, status.data, subscribe]);

  const enable = useCallback(() => {
    setLocalError(null);
    setWorking(true);

    void (async () => {
      try {
        const granted = await Notification.requestPermission();
        setPermission(granted);
        if (granted !== 'granted') return;

        const applicationServerKey = key.data;
        if (!applicationServerKey) {
          throw new Error('Server has no VAPID key');
        }

        const registration = await registerServiceWorker();
        await navigator.serviceWorker.ready;

        const subscription =
          (await registration.pushManager.getSubscription()) ??
          (await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: toApplicationServerKey(applicationServerKey),
          }));

        await subscribe.mutateAsync(toInput(subscription));
      } catch (err) {
        console.error('Push subscribe failed', err);
        setLocalError(err);
      } finally {
        setWorking(false);
      }
    })();
  }, [key.data, subscribe]);

  const disable = useCallback(() => {
    setLocalError(null);
    setWorking(true);

    void (async () => {
      try {
        const subscription = await currentSubscription();
        if (subscription) {
          await unsubscribe.mutateAsync(subscription.endpoint);
          await subscription.unsubscribe();
        }
      } catch (err) {
        setLocalError(err);
      } finally {
        setWorking(false);
      }
    })();
  }, [unsubscribe]);

  let state: PushState;
  if (!supported) state = 'unsupported';
  else if (!secure) state = 'insecure';
  else if (key.data === null) state = 'unavailable';
  else if (permission === 'denied') state = 'denied';
  else state = (status.data?.devices ?? 0) > 0 && permission === 'granted' ? 'on' : 'off';

  return {
    state,
    permission,
    devices: status.data?.devices ?? 0,
    ready: !key.isPending && !status.isPending,
    busy: working || key.isPending || status.isPending,
    error: localError ?? subscribe.error ?? unsubscribe.error ?? test.error,
    testSent: test.isSuccess,
    enable,
    disable,
    sendTest: () => test.mutate(),
  };
}
